import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import classnames from '../../../../core/utils/classnames.tsx';
import { useTranslation } from 'react-i18next';
import Page from '../../../../core/components/Page';
import OrderByDropdown from '../../../../core/components/OrderByDropdown.tsx';
import {
    buildFilterQuery,
    IFilter,
    IStandaloneFilter,
    ORDER_BY_FILTER_DEFAULT_VALUE,
    ORDER_BY_FILTER_KEY,
    SEARCH_FILTER_KEY,
    USER_FILTER_URL_PARAM_KEY,
} from '../../../../core/utils/filterQuery';
import { withIcon } from '../../../../core/components/Icon';
import { faArchive, faBarsFilter, faFlask, faInbox } from '@fortawesome/pro-regular-svg-icons';
import { useOverviewContext } from './OverviewContext';
import SearchField from '../../../../core/components/SearchField.tsx';
import StatusFilter from './StatusFilter';
import UserFilter from './UserFilter';
import AssignToFilter from './AssignToFilter';
import PastDateFilter from './DateFilter';
import { useApplicationContext } from '../../../../core/contexts/ApplicationContext';
import { canSeeTestingDocuments } from '../../../../users/utils';
import { useDebounce } from '../../../../core/utils/hooks/useDebounce';
import ClientFilter from './ClientFilter';
import { endOfDay, format, formatISO, isValid, parse, parseISO, startOfDay } from 'date-fns';
import ClientTypeFilter from './ClientTypeFilter.tsx';
import { UserFilterName } from '../../../constants.ts';

const FilterIcon = withIcon(faBarsFilter);
const InboxIcon = withIcon(faInbox);
const ArchiveIcon = withIcon(faArchive);
const TestingIcon = withIcon(faFlask);

interface FilterBarProps {
    filters: IFilter[];
    onFiltersChange: (filters: IFilter[]) => void;
    onFiltersClear?: () => void;
    // used for db_immo external status right now
    renderExtraFilters?: (props: {
        filters: IFilter[];
        onFiltersChange: (filters: IFilter[]) => void;
    }) => React.ReactNode;
    activeChannel: any;
    channels: any;
    finished: boolean;
}

export const TabFilter = ({
    active = false,
    className,
    ...props
}: React.ComponentPropsWithoutRef<typeof Link> & { active?: boolean }) => (
    <Link
        className={classnames(
            'border-b-2 border-solid border-line-transparent flex items-center justify-center gap-2 px-3 font-medium text-sm text-content-strong hover:text-content-brand-strong transition-colors',
            'hover:border-line-hover hover:text-content-strong',
            active && '!border-line-brand-focus !text-content-brand-strong',
            className
        )}
        {...props}
    />
);

export const convertToDateTimeFilters = (filters: IFilter[]) => {
    const dateTimeFilters = filters.filter((f) => f.name === 'created_at' || f.name === 'finished_at');
    const remainingFilters = filters.filter((f) => f.name !== 'created_at' && f.name !== 'finished_at');
    return [
        ...remainingFilters,
        ...dateTimeFilters.map((f) => ({
            ...f,
            value: f.value.map((v) => {
                const parsedDate = parse(v, 'yyyy-MM-dd', new Date());

                const localMidnight = ['ge', 'gt'].includes(f.expression)
                    ? startOfDay(parsedDate)
                    : ['le', 'lt'].includes(f.expression)
                      ? endOfDay(parsedDate)
                      : parsedDate;

                return formatISO(localMidnight);
            }),
        })),
    ];
};

export const convertFromDateTimeFilters = (filters: IFilter[]) => {
    const dateTimeFilters = filters.filter((f) => f.name === 'created_at' || f.name === 'finished_at');
    const remainingFilters = filters.filter((f) => f.name !== 'created_at' && f.name !== 'finished_at');
    return [
        ...remainingFilters,
        ...dateTimeFilters.map((f) => ({
            ...f,
            value: f.value.map((v) => {
                const parsedDate = parseISO(v);
                return isValid(parsedDate) ? format(parsedDate, 'yyyy-MM-dd') : v;
            }),
        })),
    ];
};

const DropdownFilterBar = ({
    filters,
    onFiltersChange,
    onFiltersClear,
    renderExtraFilters,
    activeChannel,
    channels,
    finished,
}: FilterBarProps) => {
    const { t } = useTranslation('assistance');

    const listFilters = filters.filter((f) => f.name !== ORDER_BY_FILTER_KEY && f.name !== SEARCH_FILTER_KEY);

    return (
        <div className="px-6 py-4 flex flex-wrap gap-2 bg-surface-medium border-b border-solid border-line-weak">
            <div className="flex-nowrap">
                <StatusFilter filters={filters} onFiltersChange={onFiltersChange} />
            </div>
            <div className="flex-nowrap">
                <ClientFilter filters={filters} onFiltersChange={onFiltersChange} />
            </div>
            <div className="flex-nowrap">
                <ClientTypeFilter filters={filters} onFiltersChange={onFiltersChange} />
            </div>
            <div className="flex-nowrap">
                <AssignToFilter
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                    activeChannel={activeChannel}
                    channels={channels}
                    filterName={UserFilterName.ASSIGNED_TO}
                />
            </div>

            <div className="flex-nowrap">
                <UserFilter
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                    activeChannel={activeChannel}
                    channels={channels}
                    filterName={UserFilterName.CREATED_BY}
                />
            </div>

            <div className="flex-nowrap">
                <PastDateFilter
                    filters={convertFromDateTimeFilters(filters)}
                    onFiltersChange={(filters) => onFiltersChange(convertToDateTimeFilters(filters))}
                    filterName={UserFilterName.CREATED_AT}
                />
            </div>
            {finished && (
                <div className="flex-nowrap">
                    <UserFilter
                        filters={filters}
                        onFiltersChange={onFiltersChange}
                        activeChannel={activeChannel}
                        channels={channels}
                        filterName={UserFilterName.ASSISTED_BY}
                    />
                </div>
            )}
            {finished && (
                <div className="flex-nowrap">
                    <PastDateFilter
                        filters={convertFromDateTimeFilters(filters)}
                        onFiltersChange={(filters) => onFiltersChange(convertToDateTimeFilters(filters))}
                        filterName={UserFilterName.FINISHED_AT}
                    />
                </div>
            )}

            {renderExtraFilters && renderExtraFilters({ filters, onFiltersChange })}

            {listFilters.length > 0 && (
                <button
                    className="text-content-strong px-3 text-sm font-medium hover:text-content-strong hover:bg-surface-strong rounded transition-colors"
                    onClick={onFiltersClear}
                >
                    {t('overview.filters.clearAll')}
                </button>
            )}
        </div>
    );
};

const SHARED_FILTER_NAMES = [
    UserFilterName.CREATED_BY,
    UserFilterName.CREATED_AT,
    UserFilterName.CLIENT,
    UserFilterName.CLIENT_TYPE,
    UserFilterName.ORDER_BY,
    UserFilterName.SEARCH,
];

const FilterBar = ({
    filters,
    onFiltersChange,
    onFiltersClear,
    renderExtraFilters,
    activeChannel,
    channels,
}: FilterBarProps) => {
    const { t } = useTranslation('assistance');

    const { user } = useApplicationContext();
    const { channelId, documentConfiguration, finished, testing, openUrl, finishedUrl, testingUrl } =
        useOverviewContext();

    const listFilters = filters.filter((f) => f.name !== ORDER_BY_FILTER_KEY && f.name !== SEARCH_FILTER_KEY);
    const [dropdownFilterBarVisible, setDropdownFilterBarVisible] = useState(listFilters.length > 0);
    useEffect(() => {
        if (!dropdownFilterBarVisible) {
            setDropdownFilterBarVisible(listFilters.length > 0);
        }
    }, [filters]);

    const uniqueListFiltersCount = new Set(listFilters.map((f) => f.name)).size;

    let orderByValue = filters.find((f) => f.name === ORDER_BY_FILTER_KEY)?.value;
    if (orderByValue && Array.isArray(orderByValue)) orderByValue = orderByValue[0];

    const handleOrderByChange = (orderBy) => {
        // replace the current order_by filter with the new one
        const newFilters = filters.filter((f) => f.name !== ORDER_BY_FILTER_KEY);
        newFilters.push({ name: ORDER_BY_FILTER_KEY, value: orderBy, exclude: false, expression: 'eq' });
        onFiltersChange(newFilters);
    };

    let searchTerm = filters.find((f) => f.name === SEARCH_FILTER_KEY)?.value || '';
    if (searchTerm && Array.isArray(searchTerm)) searchTerm = searchTerm[0];

    const [searchInputValue, setSearchInputValue] = useState(searchTerm);

    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    const lastPropagatedRef = useRef(searchTerm);

    useEffect(() => {
        if (searchTerm !== lastPropagatedRef.current) {
            setSearchInputValue(searchTerm);
            lastPropagatedRef.current = searchTerm;
        }
    }, [searchTerm]);

    const propagateSearchFilter = useCallback(
        (value: string) => {
            lastPropagatedRef.current = value;
            const newFilters = filtersRef.current.filter((f) => f.name !== SEARCH_FILTER_KEY);
            if (value) {
                newFilters.push({ name: SEARCH_FILTER_KEY, value, exclude: false, expression: 'eq' });
            }
            onFiltersChange(newFilters);
        },
        [onFiltersChange]
    );

    const debouncedPropagateSearchFilter = useDebounce(propagateSearchFilter, 500);

    const handleSearchTermChange = (value: string) => {
        setSearchInputValue(value);
        lastPropagatedRef.current = value;
        debouncedPropagateSearchFilter(value);
    };

    const tabSearchParams = useCallback(
        (tabKey) => {
            // to generate the tab specific search params, we need to take the filters from the local storage
            // and replace the shared once with the ones from the url if given

            const storageKey = `${documentConfiguration.documentTypeName}-${channelId}-${tabKey}`;
            const localStorageFilters: (IFilter | IStandaloneFilter)[] = JSON.parse(
                localStorage.getItem(storageKey) || '[]'
            );

            const searchParams = new URLSearchParams(document.location.search);
            const sharedFilters = filters.filter((f) => SHARED_FILTER_NAMES.includes(f.name));

            // TODO: move calculation filters logic to one place
            const combinedFilters = [
                ...localStorageFilters
                    .filter((filter): filter is IFilter => !('standalone' in filter))
                    .filter((f) => !SHARED_FILTER_NAMES.includes(f.name) || f.name === ORDER_BY_FILTER_KEY),
                ...sharedFilters,
            ];

            const combinedFiltersString = buildFilterQuery(combinedFilters);
            if (combinedFiltersString) {
                searchParams.set(USER_FILTER_URL_PARAM_KEY, combinedFiltersString);
            } else {
                searchParams.delete(USER_FILTER_URL_PARAM_KEY);
            }
            return searchParams;
        },
        [filters]
    );

    const orderByOptions = () => {
        const archivedOptions = [
            {
                value: 'archived_at',
                label: t('overview.filters.orderBy.options.archivedAt'),
                isDate: true,
            },
            {
                value: 'archived_by__email',
                label: t('overview.filters.orderBy.options.archivedBy'),
            },
        ];

        const baseOptions = documentConfiguration?.orderByOptions ?? [
            {
                value: 'created_at',
                label: t('overview.filters.orderBy.options.createdAt'),
                isDate: true,
            },
            {
                value: 'created_by__email',
                label: t('overview.filters.orderBy.options.createdBy'),
            },
            {
                value: 'assigned_to__email',
                label: t('overview.filters.orderBy.options.assignedTo'),
            },
        ];

        return finished ? [...baseOptions, ...archivedOptions] : baseOptions;
    };

    return (
        <>
            <Page.Header className="py-0" primary={false}>
                <div className="flex flex-1 h-full">
                    <TabFilter to={`${openUrl}?${tabSearchParams('open').toString()}`} active={!finished && !testing}>
                        <InboxIcon /> {t('overview.header.open')}
                    </TabFilter>

                    <TabFilter to={`${finishedUrl}?${tabSearchParams('finished').toString()}`} active={finished}>
                        <ArchiveIcon /> {t('overview.header.finished')}
                    </TabFilter>

                    {testingUrl && canSeeTestingDocuments(user) && (
                        <TabFilter to={`${testingUrl}?${tabSearchParams('testing').toString()}`} active={testing}>
                            <TestingIcon /> {t('overview.header.testing')}
                        </TabFilter>
                    )}
                </div>

                <div className="py-2.5 flex gap-2">
                    <SearchField
                        placeholder={t('overview.filters.search.placeholder')}
                        value={searchInputValue}
                        onValueChange={handleSearchTermChange}
                    />
                    <Page.HeaderButton
                        active={dropdownFilterBarVisible}
                        onClick={() => setDropdownFilterBarVisible(!dropdownFilterBarVisible)}
                        variant="ghost"
                    >
                        <FilterIcon /> {t('overview.filters.toggleLabel')}{' '}
                        {uniqueListFiltersCount > 0 && `(${uniqueListFiltersCount})`}
                    </Page.HeaderButton>

                    <OrderByDropdown
                        label={t('overview.filters.orderBy.label')}
                        options={orderByOptions()}
                        selected={orderByValue}
                        defaultValue={ORDER_BY_FILTER_DEFAULT_VALUE}
                        onSelectedChange={handleOrderByChange}
                        inline
                    />
                </div>
            </Page.Header>

            {dropdownFilterBarVisible && (
                <DropdownFilterBar
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                    onFiltersClear={onFiltersClear}
                    renderExtraFilters={renderExtraFilters}
                    activeChannel={activeChannel}
                    channels={channels}
                    finished={finished}
                />
            )}
        </>
    );
};

export default FilterBar;
